import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { TICK_TYPE } from 'src/features/pricingPlan/utils/types';
import { TICK_TYPE_MAP } from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlanTierFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

type TierFormProps = {
    form: UseFormReturn<PricingPlanTierFormSchemaValues>;
    onSubmit: (values: PricingPlanTierFormSchemaValues) => void;
};

function TierForm({ onSubmit, form }: TierFormProps) {
    const { formatMessage } = useIntl();

    const [displayRate, setDisplayRate] = React.useState('');

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<PricingPlanTierFormSchemaValues>
                form={form}
                name="start_minute"
                label={formatMessage(pricingPlanMessages.startMinute)}
                type="number"
                placeholder={`${formatMessage(pricingPlanMessages.startMinute)}...`}
            />
            <InputFormField<PricingPlanTierFormSchemaValues>
                form={form}
                name="end_minute"
                label={formatMessage(pricingPlanMessages.endMinute)}
                type="number"
                placeholder={`${formatMessage(pricingPlanMessages.endMinute)}...`}
                required={false}
            />

            <FormField
                control={form.control}
                name="rate_with_tax"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.rate} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPlanMessages.rate)}...`}
                                    value={
                                        displayRate !== ''
                                            ? displayRate
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayRate(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (displayRate === '' || isNaN(parseFloat(displayRate))) return;
                                        const formatted = parseFloat(displayRate).toFixed(2);
                                        setDisplayRate(formatted);
                                        field.onChange(Math.round(parseFloat(formatted) * 100));
                                    }}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e'].includes(e.key)) e.preventDefault();
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <SelectFormField<PricingPlanTierFormSchemaValues>
                form={form}
                name="tick_type"
                label={formatMessage(pricingPlanMessages.tickTypeColumn)}
                options={Object.values(TICK_TYPE)}
                optionTranslationMap={TICK_TYPE_MAP}
            />

            <InputFormField<PricingPlanTierFormSchemaValues>
                form={form}
                name="tick_minutes"
                label={formatMessage(pricingPlanMessages.tickDuration)}
                type="number"
                placeholder={`${formatMessage(pricingPlanMessages.tickDuration)}...`}
            />
        </Form>
    );
}

export default TierForm;
